<template>
  <div class="min-h-screen bg-[#f6f7f9] py-10">
    <div class="max-w-[900px] mx-auto px-6">

      <!-- Loading -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-32">
        <div class="w-12 h-12 rounded-full border-4 border-[#f0f0f0] border-t-[#FFD500] animate-spin mb-4"></div>
        <p class="text-[#888]">Pagina laden...</p>
      </div>

      <!-- Niet gevonden -->
      <div v-else-if="notFound" class="bg-white rounded-2xl border border-[#e9eaec] shadow-sm p-12 text-center">
        <div class="w-20 h-20 rounded-full bg-[#f8f9fa] flex items-center justify-center mx-auto mb-5">
          <i class="bi bi-file-earmark-x text-[2rem] text-[#ddd]"></i>
        </div>
        <h1 class="text-[1.5rem] font-bold text-[#111] mb-3">Pagina niet gevonden</h1>
        <p class="text-[#888] mb-6 text-[0.9375rem]">
          Deze pagina bestaat nog niet of is verplaatst.<br>
          Maak hem aan in het WordPress-dashboard en hij verschijnt hier automatisch.
        </p>
        <router-link to="/" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-3 rounded-lg no-underline transition-all">
          <i class="bi bi-house"></i> Terug naar home
        </router-link>
      </div>

      <!-- Content -->
      <div v-else class="bg-white rounded-2xl border border-[#e9eaec] shadow-sm overflow-hidden">
        <!-- Gele top-balk -->
        <div class="h-1 bg-gradient-to-r from-[#FFD500] to-[#FFC107]"></div>

        <div class="px-8 py-10">

          <!-- Breadcrumb -->
          <nav class="flex items-center gap-2 text-[0.82rem] text-[#999] mb-6">
            <router-link to="/" class="hover:text-[#111] transition-colors no-underline"><i class="bi bi-house"></i></router-link>
            <i class="bi bi-chevron-right text-[0.7rem]"></i>
            <span class="text-[#111] font-medium">{{ page.title }}</span>
          </nav>

          <h1 class="text-[2rem] font-extrabold text-[#111] mb-8 leading-tight">{{ page.title }}</h1>

          <div
            class="wp-content text-[0.9375rem] text-[#444] leading-relaxed"
            v-html="page.content"
          ></div>

          <div v-if="page.modified" class="mt-10 pt-5 border-t border-[#f0f0f0] flex items-center gap-2 text-[0.8rem] text-[#aaa]">
            <i class="bi bi-calendar3"></i>
            Laatst bijgewerkt: {{ page.modified }}
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const route   = useRoute()
const loading  = ref(true)
const notFound = ref(false)
const page     = ref({ title: '', content: '', modified: '' })

// Slug bepalen: meta.wpSlug (named routes) of route.params.slug (wildcard)
const slug = computed(() => route.meta?.wpSlug || route.params?.slug || '')

const fetchPage = async () => {
  if (!slug.value) { notFound.value = true; loading.value = false; return }

  loading.value  = true
  notFound.value = false

  try {
    const res  = await fetch(`/api/wp/v2/pages?slug=${encodeURIComponent(slug.value)}&_fields=title,content,modified`)
    const data = await res.json()

    if (!Array.isArray(data) || data.length === 0) {
      notFound.value = true
      return
    }

    const wp = data[0]
    page.value = {
      title:    wp.title?.rendered   || slug.value,
      content:  wp.content?.rendered || '',
      modified: wp.modified
        ? new Date(wp.modified).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
        : '',
    }
  } catch (e) {
    console.error('[TextPage] fetch error:', e)
    notFound.value = true
  } finally {
    loading.value = false
  }
}

onMounted(fetchPage)
watch(slug, fetchPage)
</script>

<style scoped>
/* WordPress content styling */
.wp-content :deep(h2) {
  font-size: 1.35rem; font-weight: 700; color: #111;
  margin-top: 2rem; margin-bottom: 0.75rem;
  padding-bottom: 0.5rem; border-bottom: 1px solid #f0f0f0;
}
.wp-content :deep(h3) {
  font-size: 1.1rem; font-weight: 700; color: #222;
  margin-top: 1.5rem; margin-bottom: 0.5rem;
}
.wp-content :deep(p)  { margin-bottom: 1rem; }
.wp-content :deep(ul),
.wp-content :deep(ol) { padding-left: 1.25rem; margin-bottom: 1rem; }
.wp-content :deep(ul > li) { list-style-type: disc;    margin-bottom: 0.375rem; }
.wp-content :deep(ol > li) { list-style-type: decimal; margin-bottom: 0.375rem; }
.wp-content :deep(a)  { color: #b8860b; text-decoration: underline; }
.wp-content :deep(a:hover) { color: #111; }
.wp-content :deep(strong) { font-weight: 700; color: #111; }
.wp-content :deep(table) {
  width: 100%; border-collapse: collapse; margin-bottom: 1.5rem; font-size: 0.9rem;
}
.wp-content :deep(th) {
  background: #f8f9fa; font-weight: 700; text-align: left;
  padding: 0.625rem 0.875rem; border: 1px solid #e5e7eb;
}
.wp-content :deep(td) {
  padding: 0.625rem 0.875rem; border: 1px solid #e5e7eb;
}
.wp-content :deep(tr:nth-child(even) td) { background: #fafafa; }
.wp-content :deep(blockquote) {
  border-left: 3px solid #FFD500; margin: 1.5rem 0;
  padding: 0.75rem 1rem; background: #fffbe6; border-radius: 0 8px 8px 0;
  font-style: italic; color: #555;
}
.wp-content :deep(img) { max-width: 100%; border-radius: 4px; }
.wp-content :deep(hr) { border: none; border-top: 1px solid #f0f0f0; margin: 2rem 0; }
</style>
